<template>
	<transition name="v2-modal-transition">
		<div class="v2im__backdrop" @click.self="close">
			<section class="v2im__modal">

				<!-- ════════ HEADER ════════ -->
				<header class="v2im__header">
					<div class="v2im__title-group">
						<div class="v2im__icon">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>
						</div>
						<div>
							<h2 class="v2im__title">{{ $t('inventory_modal.modal_title') }}</h2>
							<p class="v2im__subtitle">{{ $t('inventory_modal.modal_description') }}</p>
						</div>
					</div>
					<div class="v2im__actions">
						<button
							class="v2im__btn v2im__btn--outline"
							:disabled="isBusy"
							@click="exportExcel"
						>
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
							{{ $t('inventory_modal.excel_export') }}
						</button>
						<button
							class="v2im__btn v2im__btn--ghost"
							:disabled="isBusy"
							@click="close"
						>
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
							{{ $t('common.close') }}
						</button>
					</div>
				</header>

				<!-- ════════ BODY ════════ -->
				<div class="v2im__body">

					<!-- Stats Grid -->
					<div v-if="currentSelection" class="v2im__stats-grid">

						<!-- Ad Plays / Impressions card -->
						<div class="v2im__stats-card">
							<div class="v2im__stats-head">
								<h3 class="v2im__stats-title">{{ adPlaysTitle }}</h3>
								<span v-if="periodLabel" class="v2im__stats-period">{{ periodLabel }}</span>
							</div>

							<div class="v2im__stats-row">
								<span class="v2im__stats-label">{{ $t('campaign.widget_budget_label') | capitalize }}</span>
								<span class="v2im__stats-label">{{ $t('campaign.available') }}</span>
							</div>
							<div class="v2im__stats-row v2im__stats-row--values">
								<span class="v2im__stats-value v2im__stats-value--primary">{{ formatNum(primaryStats.value) }}</span>
								<span class="v2im__stats-value">{{ formatNum(primaryStats.total) }}</span>
							</div>

							<div class="v2im__stats-bar">
								<div
									class="v2im__stats-bar-fill"
									:style="{ width: primaryStatsPercent + '%' }"
								/>
							</div>

							<div class="v2im__substats">
								<div>
									<p class="v2im__substat-label">{{ $t('campaign.ad_flight_days') | capitalize }}</p>
									<p class="v2im__substat-value v2im__substat-value--accent">{{ adFlight || '—' }}</p>
								</div>
								<div>
									<p class="v2im__substat-label">{{ $t('campaign.per_day') | capitalize }}</p>
									<p class="v2im__substat-value">{{ formatNum(impressionsPerDay) }}</p>
								</div>
								<div>
									<p class="v2im__substat-label">{{ $t('total') | capitalize }}</p>
									<p class="v2im__substat-value">{{ formatNum(primaryStats.value) }}</p>
								</div>
							</div>
						</div>

						<!-- Inventory / Uptime card -->
						<div class="v2im__stats-card">
							<div class="v2im__stats-head">
								<h3 class="v2im__stats-title">{{ $t('campaign.widget_inventory_title') }}</h3>
							</div>

							<div class="v2im__stats-row">
								<span class="v2im__stats-label">
									<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>
									{{ $t('campaign.selected') | capitalize }}
								</span>
								<span class="v2im__stats-label">
									<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>
									{{ $t('campaign.available') }}
								</span>
							</div>
							<div class="v2im__stats-row v2im__stats-row--values">
								<span class="v2im__stats-value v2im__stats-value--primary">{{ formatNum(inventoryValue) }}</span>
								<span class="v2im__stats-value">{{ formatNum(inventoryTotal) }}</span>
							</div>

							<div class="v2im__stats-bar">
								<div
									class="v2im__stats-bar-fill"
									:style="{ width: inventoryPercent + '%' }"
								/>
							</div>

							<div class="v2im__uptime-grid">
								<div class="v2im__uptime-cell v2im__uptime-cell--green">
									<p class="v2im__uptime-label">{{ $t('inventory_modal.good_uptime_value') }}</p>
									<p class="v2im__uptime-value">{{ formatNum(devicesUptime && devicesUptime.good_uptime) }}</p>
								</div>
								<div class="v2im__uptime-cell v2im__uptime-cell--yellow">
									<p class="v2im__uptime-label">{{ $t('inventory_modal.average_uptime_value') }}</p>
									<p class="v2im__uptime-value">{{ formatNum(devicesUptime && devicesUptime.average_uptime) }}</p>
								</div>
								<div class="v2im__uptime-cell v2im__uptime-cell--red">
									<p class="v2im__uptime-label">{{ $t('inventory_modal.bad_uptime_value') }}</p>
									<p class="v2im__uptime-value">{{ formatNum(devicesUptime && devicesUptime.bad_uptime) }}</p>
								</div>
							</div>
						</div>
					</div>

					<!-- Filter bar -->
					<div class="v2im__filter-bar">
						<div class="v2im__search-wrap">
							<svg class="v2im__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
							<input
								v-model="search"
								type="text"
								class="v2im__search-input"
								:placeholder="$t('inventory_modal.search')"
							/>
						</div>

						<select
							v-model="listSubsetType"
							class="v2im__subset-select"
							:disabled="isBusy"
						>
							<option
								v-for="opt in subsetSelectOptions"
								:key="opt.value"
								:value="opt.value"
							>{{ opt.label }}</option>
						</select>
					</div>

					<!-- Table -->
					<div class="v2im__table-wrap">
						<div ref="tableWrapper" class="v2im__table-scroll">
							<table class="v2im__table">
								<inventory-table-header-row
									:context="{ currency: instance.currency, payment_model: instance.payment_model }"
									:ordering.sync="ordering"
									:disabled="isBusy"
								/>
								<tbody v-if="deviceList && deviceList.length && !error">
									<inventory-table-row
										v-for="device in deviceList"
										:key="device.id"
										:device="device"
										:context="{ currency: instance.currency, payment_model: instance.payment_model, request_id: instance.id }"
										@update-required="updateManualFilter"
									/>
								</tbody>
							</table>

							<!-- Error -->
							<div v-if="error" class="v2im__error">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
								<span v-html="$t('inventory_modal.error_message', error)" />
							</div>

							<!-- Empty -->
							<div v-else-if="!isBusy && (!deviceList || !deviceList.length)" class="v2im__empty">
								<div class="v2im__empty-icon">
									<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
								</div>
								<p class="v2im__empty-text">{{ $t('inventory_modal.no_devices_message') }}</p>
							</div>
						</div>

						<!-- Pagination -->
						<div v-if="deviceList && deviceList.length && !error" class="v2im__pagination">
							<TablePaginator
								:value="pagination"
								:total="totalDevicesCount"
								:disabled="isBusy"
								:page-size-options="[6, 20, 40]"
								@input="handlePaginationChange"
							/>
						</div>
					</div>

				</div>
			</section>
		</div>
	</transition>
</template>


<script>
import OriginalModal from '@/ui-overrides/default/pages/request/create/inventory-details-modal/index.vue';
import InventoryTableHeaderRow from './inventory-table-header-row.vue';
import InventoryTableRow from './inventory-table-row.vue';

import { PAYMENT_MODEL_CPM, PAYMENT_MODEL_CPV } from '@/constants/payment_models';

export default {
	name: 'V2InventoryDetailsModal',

	extends: OriginalModal,

	components: {
		...OriginalModal.components,
		InventoryTableHeaderRow,
		InventoryTableRow,
	},

	computed: {
		adPlaysTitle() {
			const pm = this.instance && this.instance.payment_model;
			if (pm === PAYMENT_MODEL_CPM || pm === PAYMENT_MODEL_CPV) {
				return this.$t('campaign.widget_impressions_title');
			}
			return this.$t('campaign.widget_ad_plays_title');
		},

		primaryStats() {
			if (!this.currentSelection) return { value: 0, total: 0 };
			const pm = this.instance && this.instance.payment_model;
			if (pm === PAYMENT_MODEL_CPM || pm === PAYMENT_MODEL_CPV) {
				return this.currentSelection.impressions || { value: 0, total: 0 };
			}
			return this.currentSelection.plays || { value: 0, total: 0 };
		},

		primaryStatsPercent() {
			const v = this.primaryStats.value || 0;
			const t = this.primaryStats.total || 0;
			if (!t) return 0;
			return Math.min(100, Math.round((v / t) * 100));
		},

		inventoryValue() {
			return (this.currentSelection && this.currentSelection.inventory && this.currentSelection.inventory.value) || 0;
		},

		inventoryTotal() {
			return (this.currentSelection && this.currentSelection.inventory && this.currentSelection.inventory.total) || 0;
		},

		inventoryPercent() {
			if (!this.inventoryTotal) return 0;
			return Math.min(100, Math.round((this.inventoryValue / this.inventoryTotal) * 100));
		},

		periodLabel() {
			const tp = this.instance && this.instance.time_period;
			if (!tp || !tp.start || !tp.end) return '';
			const fmt = (d) => {
				try {
					const dt = new Date(d);
					return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
				} catch (e) {
					return '';
				}
			};
			return `${fmt(tp.start)} — ${fmt(tp.end)}`;
		},

		adFlight() {
			const tp = this.instance && this.instance.time_period;
			if (!tp || !tp.start || !tp.end) return null;
			const start = new Date(tp.start);
			const end = new Date(tp.end);
			const ms = end - start;
			if (isNaN(ms) || ms < 0) return null;
			return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
		},
	},

	methods: {
		formatNum(n) {
			if (n === null || n === undefined) return '0';
			if (typeof n !== 'number') return n;
			return n.toLocaleString();
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

$teal: $v2-teal
$purple: $v2-purple
$text: $v2-text
$muted: $v2-text-muted
$border: $v2-border
$card: $v2-card-bg
$bg: $v2-bg
$green: $v2-green
$orange: $v2-orange
$red: $v2-red
$font: $v2-font
$mono: 'JetBrains Mono', 'Menlo', monospace

// ── Modal backdrop ─────────────────
.v2im__backdrop
	position: fixed
	inset: 0
	background: rgba(15, 23, 42, 0.45)
	backdrop-filter: blur(4px)
	-webkit-backdrop-filter: blur(4px)
	display: flex
	align-items: flex-start
	justify-content: center
	padding: 40px 24px
	overflow-y: auto
	z-index: 1000
	font-family: $font

.v2im__modal
	background: $card
	border-radius: 16px
	width: 100%
	max-width: 1280px
	box-shadow: 0 24px 64px -12px rgba(0,0,0,0.25)
	overflow: hidden

// ── Header ─────────────────────────
.v2im__header
	display: flex
	align-items: center
	justify-content: space-between
	padding: 20px 24px
	border-bottom: 1px solid rgba($border, 0.6)
	gap: 16px

.v2im__title-group
	display: flex
	align-items: center
	gap: 12px

.v2im__icon
	width: 40px
	height: 40px
	border-radius: 12px
	background: rgba($teal, 0.1)
	color: $teal
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0

	svg
		width: 18px
		height: 18px

.v2im__title
	font-size: 18px
	font-weight: 700
	color: $text
	letter-spacing: -0.01em

.v2im__subtitle
	font-size: 12px
	color: $muted
	margin-top: 2px

.v2im__actions
	display: flex
	gap: 8px
	flex-shrink: 0

// ── Buttons ────────────────────────
.v2im__btn
	display: inline-flex
	align-items: center
	gap: 6px
	height: 36px
	padding: 0 14px
	border-radius: 10px
	font-size: 12px
	font-weight: 600
	font-family: $font
	transition: all 0.15s
	border: 1px solid transparent
	background: transparent
	color: $text
	cursor: pointer

	svg
		width: 14px
		height: 14px

	&:disabled
		opacity: 0.5
		cursor: not-allowed

	&--outline
		background: $card
		border-color: $border

		&:hover:not(:disabled)
			background: rgba($bg, 0.5)

	&--ghost
		color: $muted
		padding: 0 10px

		&:hover:not(:disabled)
			background: rgba($bg, 0.5)
			color: $text

// ── Body ───────────────────────────
.v2im__body
	padding: 24px

// ── Stats grid ─────────────────────
.v2im__stats-grid
	display: grid
	grid-template-columns: 1fr 1fr
	gap: 16px
	margin-bottom: 24px

	@media (max-width: 768px)
		grid-template-columns: 1fr

.v2im__stats-card
	border: 1px solid rgba($border, 0.6)
	border-radius: 14px
	padding: 20px
	background: $card
	box-shadow: 0 1px 3px rgba(0,0,0,0.03)

.v2im__stats-head
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 16px

.v2im__stats-title
	font-size: 13px
	font-weight: 700
	color: $text

.v2im__stats-period
	font-size: 11px
	color: $muted
	font-family: $mono

.v2im__stats-row
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 10px

	&--values
		margin-bottom: 0

.v2im__stats-label
	font-size: 10px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.06em
	color: $muted
	display: inline-flex
	align-items: center
	gap: 4px

.v2im__stats-value
	font-family: $mono
	font-size: 18px
	font-weight: 700
	color: $text

	&--primary
		color: $teal

// Progress bar
.v2im__stats-bar
	height: 6px
	background: rgba($bg, 0.8)
	border-radius: 9999px
	overflow: hidden
	margin: 10px 0 16px

.v2im__stats-bar-fill
	height: 100%
	background: $teal
	transition: width 0.3s

// Sub-stats
.v2im__substats
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 12px
	padding-top: 12px
	border-top: 1px solid rgba($border, 0.4)

.v2im__substat-label
	font-size: 9px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.08em
	color: $muted
	margin-bottom: 4px

.v2im__substat-value
	font-family: $mono
	font-size: 15px
	font-weight: 700
	color: $text

	&--accent
		color: $purple

// Uptime breakdown
.v2im__uptime-grid
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 8px
	padding-top: 12px
	border-top: 1px solid rgba($border, 0.4)

.v2im__uptime-cell
	text-align: center
	padding: 8px
	border-radius: 8px

	&--green
		background: rgba($green, 0.08)
		color: $green

	&--yellow
		background: rgba($orange, 0.10)
		color: darken($orange, 8%)

	&--red
		background: rgba($red, 0.08)
		color: $red

.v2im__uptime-label
	font-size: 9px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.06em

.v2im__uptime-value
	font-family: $mono
	font-size: 16px
	font-weight: 700
	margin-top: 2px

// ── Filter bar ─────────────────────
.v2im__filter-bar
	display: flex
	align-items: center
	gap: 12px
	margin-bottom: 16px

	@media (max-width: 640px)
		flex-direction: column
		align-items: stretch

.v2im__search-wrap
	position: relative
	flex: 1
	max-width: 360px

.v2im__search-icon
	position: absolute
	left: 12px
	top: 50%
	transform: translateY(-50%)
	width: 14px
	height: 14px
	color: $muted
	pointer-events: none

.v2im__search-input
	width: 100%
	height: 38px
	padding: 0 14px 0 36px
	border: 1px solid $border
	border-radius: 10px
	background: rgba($bg, 0.5)
	font-size: 13px
	font-family: $font
	color: $text
	box-sizing: border-box

	&::placeholder
		color: rgba($muted, 0.7)

	&:focus
		outline: none
		border-color: $teal
		background: $card
		box-shadow: 0 0 0 3px rgba($teal, 0.12)

.v2im__subset-select
	height: 38px
	padding: 0 32px 0 14px
	border: 1px solid $border
	border-radius: 10px
	background: $card
	font-size: 13px
	font-weight: 500
	font-family: $font
	color: $text
	appearance: none
	min-width: 220px
	cursor: pointer
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")
	background-repeat: no-repeat
	background-position: right 10px center
	box-sizing: border-box

	&:focus
		outline: none
		border-color: $teal
		box-shadow: 0 0 0 3px rgba($teal, 0.12)

	&:disabled
		opacity: 0.5
		cursor: not-allowed

// ── Table ──────────────────────────
.v2im__table-wrap
	border: 1px solid rgba($border, 0.6)
	border-radius: 14px
	overflow: hidden
	background: $card
	box-shadow: 0 1px 3px rgba(0,0,0,0.03)

.v2im__table-scroll
	overflow-x: auto

.v2im__table
	width: 100%
	border-collapse: collapse
	font-size: 12px

	::v-deep thead
		background: rgba($bg, 0.6)
		border-bottom: 1px solid $border

	::v-deep th
		padding: 10px 12px
		text-align: left
		font-size: 10px
		font-weight: 700
		text-transform: uppercase
		letter-spacing: 0.06em
		color: $muted
		white-space: nowrap

	::v-deep td
		padding: 12px
		border-bottom: 1px solid rgba($border, 0.5)
		color: $text
		vertical-align: middle

	::v-deep tbody tr:last-child td
		border-bottom: none

	::v-deep tbody tr:hover td
		background: rgba($bg, 0.3)

// ── Error ──────────────────────────
.v2im__error
	display: flex
	align-items: center
	gap: 10px
	padding: 16px 20px
	color: $red
	font-size: 13px

	svg
		width: 16px
		height: 16px
		flex-shrink: 0

// ── Empty ──────────────────────────
.v2im__empty
	padding: 60px 20px
	text-align: center

.v2im__empty-icon
	width: 56px
	height: 56px
	border-radius: 14px
	background: rgba($bg, 0.7)
	color: $muted
	display: flex
	align-items: center
	justify-content: center
	margin: 0 auto 12px

.v2im__empty-text
	font-size: 13px
	color: $muted

// ── Pagination ─────────────────────
.v2im__pagination
	padding: 12px 16px
	border-top: 1px solid $border
	background: rgba($bg, 0.3)

	::v-deep .table-paginator
		display: flex
		align-items: center
		justify-content: space-between
		gap: 12px

// ── Transition ─────────────────────
.v2-modal-transition-enter-active,
.v2-modal-transition-leave-active
	transition: opacity 0.2s ease

.v2-modal-transition-enter,
.v2-modal-transition-leave-to
	opacity: 0
</style>
